/**
 * 需求来源：docs/旅游落地页/AI提示词.md 第1次提交 需求4（任务单 DEV-120352）；
 *          视觉基准 docs/旅游落地页/cj商旅-01.html 第 1003~1236 行（.cjbt-v4）与第 1237~1439 行内联脚本。
 * 注意事项：
 * 1. 右侧双列图轨为「无缝无限滚动」：原型用 JS cloneNode 复制一份 group 再用 requestAnimationFrame 位移；
 *    此处改为 **JSX 直接渲染两份 group**（第二份 aria-hidden + alt="" + loading=lazy，与原型克隆结果等价），
 *    避免 SSR 首屏只有一份、hydration 后 DOM 被 JS 追加导致的抖动与结构不一致。
 * 2. 位移算法逐行照搬原型：deltaSeconds 上限 0.05；up 向上减、越界 += distance，down 反之；
 *    distance = 单组高度 + 轨道 gap；页面不可见 / 区块移出视口 / prefers-reduced-motion 时暂停（原型同款三道开关）。
 * 3. 等图片全部 load/error 后再 measure，避免图未加载时高度测量为 0 导致不滚动（原型同款 pendingImages 计数）。
 */
import React, { useRef, useEffect } from 'react';
import { ArrowRight, CheckCircle } from './Icons';

/** 渲染一组图片卡片；clone=true 时对应原型克隆组（无障碍隐藏、alt 置空、强制懒加载）。 */
function ImageGroup(props) {
  var items = props.items;
  var clone = props.clone;
  return (
    <div className="cjbt-v4__group" aria-hidden={clone ? 'true' : undefined}>
      {items.map(function (item, index) {
        return (
          <article className="cjbt-v4__image-card" key={index}>
            <img
              src={item.src}
              alt={clone ? '' : item.alt}
              loading={clone ? 'lazy' : item.loading}
              decoding="async"
            />
            <div className="cjbt-v4__image-meta">
              <span className="cjbt-v4__image-number">{item.city}</span>
            </div>
          </article>
        );
      })}
    </div>
  );
}

export default function Hero(props) {
  var HERO = props.data;
  var rootRef = useRef(null);

  useEffect(function () {
    var root = rootRef.current;
    if (!root || typeof window === 'undefined') return undefined;

    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return undefined;

    var sliders = [];
    var isRootVisible = true;
    var isPageVisible = !document.hidden;
    var animationFrameId = null;
    var previousTime = window.performance.now();
    var resizeTimer = null;
    var observer = null;

    Array.prototype.forEach.call(root.querySelectorAll('.cjbt-v4__track'), function (track) {
      var originalGroup = track.querySelector('.cjbt-v4__group');
      if (!originalGroup) return;
      sliders.push({
        track: track,
        originalGroup: originalGroup,
        direction: track.dataset.cjbtDirection === 'down' ? 'down' : 'up',
        speed: Math.max(10, Number(track.dataset.cjbtSpeed) || 28),
        distance: 0,
        position: 0,
      });
    });

    function measureSliders() {
      sliders.forEach(function (slider) {
        var computedTrack = window.getComputedStyle(slider.track);
        var trackGap = parseFloat(computedTrack.rowGap) || parseFloat(computedTrack.gap) || 0;
        slider.distance = slider.originalGroup.getBoundingClientRect().height + trackGap;
        slider.position = slider.direction === 'down' ? -slider.distance : 0;
        slider.track.style.transform = 'translate3d(0,' + slider.position + 'px,0)';
      });
    }

    function animate(currentTime) {
      var deltaSeconds = Math.min((currentTime - previousTime) / 1000, 0.05);
      previousTime = currentTime;

      if (isRootVisible && isPageVisible) {
        sliders.forEach(function (slider) {
          if (!slider.distance) return;
          var movement = slider.speed * deltaSeconds;
          if (slider.direction === 'up') {
            slider.position -= movement;
            if (slider.position <= -slider.distance) slider.position += slider.distance;
          } else {
            slider.position += movement;
            if (slider.position >= 0) slider.position -= slider.distance;
          }
          slider.track.style.transform = 'translate3d(0,' + slider.position.toFixed(3) + 'px,0)';
        });
      }

      animationFrameId = window.requestAnimationFrame(animate);
    }

    function startAnimation() {
      if (animationFrameId !== null) return;
      previousTime = window.performance.now();
      animationFrameId = window.requestAnimationFrame(animate);
    }

    // 图片尺寸决定轨道高度，必须等全部 load/error 后再测量
    var images = root.querySelectorAll('.cjbt-v4__image-card img');
    var pendingImages = images.length;
    var started = false;
    var fallbackTimer = null;

    function startOnce() {
      if (started) return;
      started = true;
      window.clearTimeout(fallbackTimer);
      measureSliders();
      startAnimation();
    }

    function imageReady() {
      pendingImages -= 1;
      if (pendingImages <= 0) startOnce();
    }

    if (!pendingImages) {
      startOnce();
    } else {
      Array.prototype.forEach.call(images, function (image) {
        if (image.complete) {
          imageReady();
        } else {
          image.addEventListener('load', imageReady, { once: true });
          image.addEventListener('error', imageReady, { once: true });
        }
      });
      // 兜底：原型「等全部图片 load 完再启动」的写法有卡死风险——轨道里过半图片是 loading="lazy"
      // （含为无缝滚动而复制的那一组），位于视口外时浏览器可能一直不加载，load/error 都不触发，
      // pendingImages 永不归零 → 滚动永不启动（PC 宽屏实测复现：轨道 transform 恒为 0）。
      // 卡片高度由 CSS 变量 --cjbt-card-height 固定，不依赖图片解码尺寸，故超时后直接测量是安全的。
      fallbackTimer = window.setTimeout(startOnce, 1200);
    }

    function onResize() {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(measureSliders, 160);
    }
    function onVisibilityChange() {
      isPageVisible = !document.hidden;
      previousTime = window.performance.now();
    }

    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVisibilityChange);

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.target === root) {
            isRootVisible = entry.isIntersecting;
            previousTime = window.performance.now();
          }
        });
      }, { threshold: 0.02 });
      observer.observe(root);
    }

    return function () {
      if (animationFrameId !== null) window.cancelAnimationFrame(animationFrameId);
      window.clearTimeout(resizeTimer);
      window.clearTimeout(fallbackTimer);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (observer) observer.disconnect();
    };
  }, []);

  return (
    <section
      className="cjbt-v4"
      aria-label="CJ Business Travel"
      ref={rootRef}
      /* 第2次补充：背景大图随公共资源仓发布，地址需运行时 cdnHost 拼接，CSS 内无法取得，
         故在根节点注入 CSS 变量（值已含 url(...)），index.less 侧用 var(--cjbt-hero-bg)。 */
      style={{ '--cjbt-hero-bg': HERO.backgroundCss }}
    >
      <div className="cjbt-v4__container">

        {/* LEFT CONTENT */}
        <div className="cjbt-v4__content">

          <div className="cjbt-v4__eyebrow">
            <span className="cjbt-v4__eyebrow-mark"></span>
            {HERO.eyebrow}
          </div>

          <h1 className="cjbt-v4__title">
            <span className="cjbt-v4__title-line">
              {HERO.titleLead}{' '}
              <span className="cjbt-v4__title-accent">{HERO.titleAccent}</span>
            </span>
            <span className="cjbt-v4__title-bottom">{HERO.titleBottom}</span>
          </h1>

          <div className="cjbt-v4__divider"></div>

          <p className="cjbt-v4__description">{HERO.description}</p>

          <div className="cjbt-v4__features">
            {HERO.features.map(function (feature, index) {
              return <span className="cjbt-v4__feature" key={index}>{feature}</span>;
            })}
          </div>

          <div className="cjbt-v4__actions">
            <a className="cjbt-v4__button cjbt-v4__button--primary" href={HERO.primaryCtaHref}>
              <span>{HERO.primaryCta}</span>
              <ArrowRight />
            </a>
            <a className="cjbt-v4__button cjbt-v4__button--secondary" href={HERO.secondaryCtaHref}>
              <span>{HERO.secondaryCta}</span>
              <ArrowRight />
            </a>
          </div>

          <div className="cjbt-v4__support">
            <span className="cjbt-v4__support-icon"><CheckCircle /></span>
            <span>{HERO.support}</span>
          </div>

        </div>

        {/* RIGHT VISUAL */}
        <div className="cjbt-v4__visual">
          <div className="cjbt-v4__visual-shell"></div>

          <span className="cjbt-v4__decor cjbt-v4__decor--orange"></span>
          <span className="cjbt-v4__decor cjbt-v4__decor--white"></span>

          <div className="cjbt-v4__image-grid">
            {HERO.columns.map(function (column, columnIndex) {
              return (
                <div className="cjbt-v4__image-column" key={columnIndex}>
                  <div
                    className="cjbt-v4__track"
                    data-cjbt-direction={column.direction}
                    data-cjbt-speed={column.speed}
                  >
                    <ImageGroup items={column.items} />
                    <ImageGroup items={column.items} clone />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </section>
  );
}
